<template>
  <div class="tracking-view-container">
    
    <div v-if="!isCpfValidated" :class="`login-root login-theme-${brand.class_name}`">
      
      <div class="login-image">
        <img
          :alt="`${brand.name} Logo`"
          :src="getBackgroundImg"
        />
      </div>
      
      <div class="fundo">
        <div class="login-form">
          <form @submit.prevent="handleConsultOrder">
            <div class="logo-container">
              <img
                :alt="`${brand.name} Logo`"
                :src="requireImage(`theme/${brand.folder}/logo.png`)"
              />
            </div>
            
            <h4 class="title-tracking">Consulta de Tracking de Pedidos</h4>
            
            <div class="form-inputs-container">
              <div class="form-group">
                <label>{{ $t('dass.numeroPedido') || 'Número do Pedido' }}</label>
                <input 
                  type="text" 
                  :value="orderCode" 
                  disabled 
                  readonly 
                  class="input-disabled"
                />
              </div>
              
              <div class="form-group">
                <label>{{ $t('dass.typeYourCpf') || 'CPF' }}</label>
                <input 
                  type="text" 
                  v-model="inputCpf" 
                  @input="applyCpfMask"
                  required
                  maxlength="14"
                  placeholder="000.000.000-00"
                  :class="{'input-error': showCpfError}"
                />
                <span v-if="showCpfError" class="error-msg">
                  CPF inválido. Verifique os números digitados.
                </span>
              </div>
            </div>

            <div class="recaptcha-warning">
              {{ $t('dass.recaptchaWarning') || 'Protegido por reCAPTCHA' }}
            </div>

            <dass-button
              :class="isSafari ? `dass-button-acesso-rapido-${brandName} submit-btn` : 'submit-btn'"
              variant="primary"
              type="submit"
              :loading="isLoading"
              :disabled="!isValidCpf || isLoading"
            >
              {{ $t('dass.consultOrder') }}
            </dass-button>
          </form>
          
          <div class="duvidas">
            <div>
              <b>
                <span> {{ $t('login.duvidas') }}? </span>
                <dass-link :to="redirectContactUs()">
                   {{ $t('dass.cliqueAqui') }}
                </dass-link>
              </b>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="dass-tracking-order">
      <OrderTrackingHeader
        :productImage="order?.LINK_IMAGEM"
        :productTitle="order?.PRO_NOME"
        :orderDate="order?.DATA"
        :orderNumber="order?.PED_CODIGO"
        :statusPed="getStatus"
      />
      <dass-divider />
      <div class="dass-tracking-order-grid">
        <div class="dass-tracking-order-details">
          <StatusStepInfo 
            v-if="order"
            :statusPed="getStatus"
            :address="`${order?.CLI_ENDERECO} / ${order?.CLI_ENDNUM} - ${order?.CLI_CIDADE}`"
            :carrier="order?.TRA_NOME"
            :estimate="order?.DATA_PREVISTA"
            :deliveredAt="order?.DATA_ENTREGA"
            :urlCarrier="order?.URL_TRANSP" 
            :order="order"
            :isPublic="true"
          />
        </div>
        <div class="dass-tracking-order-timeline">
          <TimelineInfoDetailsOrder /> 
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrderTrackingHeader from '../components/organisms/trackingOrder/OrderTrackingHeader.vue'
import StatusStepInfo from '@/components/organisms/trackingOrder/StatusStepInfo.vue';
import TimelineInfoDetailsOrder from '@/components/organisms/trackingOrder/TimelineInfoDetailsOrder.vue';
import { mapGetters, mapActions } from 'vuex';
import { getConfig } from '../configs/service'
import requireImage from '../helpers/functions/requireImage';
import { useIsSafari } from '@/composables/useIsSafari';
import { cpfValidator } from '@/helpers/functions/cpfValidator'; 
import { buildImageUrl } from '@/helpers/functions/buildImageUrl';

export default {
  name: 'PublicTracking',
  components: {
    OrderTrackingHeader,
    StatusStepInfo,
    TimelineInfoDetailsOrder
  }, 
  setup() {
    const { isSafari, brandName } = useIsSafari()
    return { isSafari, brandName }     
  },
  data() {
    return {
      inputCpf: '',
      isCpfValidated: false,
      isLoading: false,
      recaptchaKey: '',
      is_mobile: false,
      ecommImgUrl: '',
    }
  },
  computed: {
    ...mapGetters('brand', { brand: 'getBrand' }),
    ...mapGetters('orders', {
      orders: 'getOrders',
      getOccurrences: 'getOccurrences',
    }),
    cleanCpf() {
      return this.inputCpf.replace(/\D/g, '');
    },
    isValidCpf() {
      if (this.cleanCpf.length < 11) return false;
      return cpfValidator(this.cleanCpf);
    },
    showCpfError() {
      return this.cleanCpf.length === 11 && !this.isValidCpf;
    },

    orderCode() {      
      return this.$route.params.ped_codigo || '';
    },
    order() {
      return this.orders.find((item) => item.PED_CODIGO == this.orderCode)
    },
    getOcurrencesOrder() {      
      return this.getOccurrences?.filter((item) => item?.FAT_SEQUENCIA == this.order?.FAT_SEQUENCIA) || []
    },
    getStatus() {      
      return this.order?.STATUS_PED    
    },
    getBackgroundImg() {
      return buildImageUrl(this.ecommImgUrl, this.brand?.folder, 'fundo_login', this.is_mobile, 'png');
    },
  },  
  methods: {
    requireImage,
    ...mapActions('orders', ['loadPublicTrackingOrder']),
    ...mapActions('toasts', ['showToast']),  
    
    async handleConsultOrder() {      
      if (!this.isValidCpf) return;
      
      this.isLoading = true;
      try {
        const recaptchaToken = await new Promise((resolve, reject) => {
          if (typeof window.grecaptcha === 'undefined') {
            reject(new Error('reCAPTCHA script não carregou a tempo.'));
            return;
          }
          window.grecaptcha.ready(() => {
            window.grecaptcha.execute(this.recaptchaKey, { action: 'tracking_order' })
              .then(resolve)
              .catch(reject);
          });
        });    

        await this.loadPublicTrackingOrder({
          ped_codigo: this.orderCode,
          cpf: this.cleanCpf, 
          recaptchaToken
        });

        this.isCpfValidated = true;

      } catch (error) {
        this.showToast({ 
          variant: 'error',
          message: error?.detail || error
        });
      } finally {
        this.isLoading = false;
      }
    },
    applyCpfMask(event) {
      let value = event.target.value.replace(/\D/g, '');
      if (value.length > 11) value = value.slice(0, 11);
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      this.inputCpf = value;
    },
    redirectContactUs() {      
      if (!this.brand || !this.brand.name) return '#'
      const brandClean = this.brand.name.replace(/\s/g, '').toLowerCase()
      return `https://ajuda.${brandClean}.com.br/`
    },
  },
  async mounted() {
    let current_width = document.body.clientWidth
    if (current_width < 576) this.is_mobile = true;
    try {
      const config = await getConfig();      
      this.recaptchaKey = config.VUE_APP_RECAPTCHA_KEY;
      this.ecommImgUrl = config.VUE_APP_ECOMMERCE_IMAGES_URL;
      if (!document.getElementById('recaptcha-script-tag')) {
        const script = document.createElement('script');
        script.id = 'recaptcha-script-tag';
        script.src = `https://www.google.com/recaptcha/api.js?render=${this.recaptchaKey}`;
        script.async = true;
        document.head.appendChild(script);
      }
    } catch (error) {
      console.error('Erro ao carregar a configuração do reCAPTCHA', error);      
    }
  },
}
</script>

<style lang="scss" scoped>
.tracking-view-container {
  width: 100%;
  min-height: 100vh;
}
.dass-tracking-order { 
  width: 100%;
  padding: 1.5% 3%; 
}
.dass-tracking-order-grid { 
  display: grid;
  grid-template-columns: 65% 35%;
  gap: 15px;
}
.dass-tracking-order-details, .dass-tracking-order-timeline { 
  width: 100%; 
  background: white; 
}
.footer-support {
  margin-top: 3rem;
  display: flex;
  justify-content: center;
}
@media (max-width: 1050px) { 
  .dass-tracking-order-grid { 
    grid-template-columns: 100%; 
  } 
}

.login-root {
  position: relative;
  height: 100vh;
  overflow: hidden;

  .login-image {
    background: var(--color-secondary--blue__lighter);
    height: 100%;
    width: 100%;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;      
    }
  }

  .fundo {
    position: absolute;
    height: 100%;
    right: 0;
    top: 0;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    @media (max-width: 576px) {
      justify-content: center;
      align-items: center;
    }
    .login-form {
      border: 1px solid var(--theme-gray_light);
      border-radius: 20px 0px 0px 20px;
      background: var(--theme-white);
      width: 60%;
      max-width: 500px;
      @media (max-width: 700px) {
        width: 90%;                
        border-radius: 20px;
      }
      form {
        padding: var(--space__3x);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 88%;
        @media (max-width: 576px) {
          justify-content: flex-start;
          padding: var(--space__1-5x);
          padding-top: 15%;
        }
        .logo-container {
          display: flex;
          justify-content: center;
          width: 60%;
          img {
            width: 60%;
            height: auto;
          }
        }
        
        .title-tracking {
          text-align: center;
          margin: 1.5rem 0 1rem 0;
        }

        .form-inputs-container {
          width: 100%;
          padding: 0 1rem;
        }

        .form-group {
          position: relative;
          margin-bottom: 1.5rem;
          width: 100%;
          
          label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
            font-size: 0.9rem;
          }
          input {
            width: 100%;
            padding: 0.8rem;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 1rem;
            outline: none;
            transition: border-color 0.2s ease;
            
            &:focus {
              border-color: var(--theme-color, #000);
            }
          }
          .input-disabled {
            background-color: #f5f5f5;
            color: #666;
            cursor: not-allowed;
            &:focus { border-color: #ccc; }
          }          
          .input-error {
            border-color: #d32f2f !important;
          }
          
          .error-msg {
            color: #d32f2f;
            font-size: 0.8rem;
            position: absolute;
            bottom: -1.2rem;
            left: 0;
          }
        }

        .recaptcha-warning {
          font-size: 0.75rem;
          color: #888;
          text-align: center;
          margin-top: -0.5rem;
          margin-bottom: 1rem;
        }

        .submit-btn {
          width: 100%;
          margin: var(--space__0-75x) 0;          
        }
      }
      
      .duvidas {
        width: 100%;
        text-align: center;
        > div:last-of-type {
          margin-top: var(--space__0-75x);
          font-size: 0.8rem;
          line-height: 1rem !important;
          span {
            margin-right: var(--space__0-5x);
          }
        }
      }
    }
  }

  &.login-theme-umbro {    
    .login-form {
      color: var(--theme-white);
      border: none;
      background-color: var(--theme-color);

      @media (max-width: 576px) {
        height: 50%;
        padding-bottom: 2rem;
      }

      .title-tracking {
        color: var(--theme-white);
      }

      .logo-container {
        width: 30% !important;
      }

      .submit-btn {
        background-color: var(--theme-white) !important;
        color: var(--theme-color);
      }
      .dass-link {
        color: var(--theme-white) !important;
      }
    }

    .form-group {
      label {
        position: absolute;
        z-index: 15;
        background: var(--theme-color);
        top: -0.6rem;
        left: 0.5rem;
        padding: 0 0.5rem;
        font-size: 0.8rem;
      }
      input {
        background-color: var(--theme-color) !important;
        color: var(--theme-white);
        border-color: #666;
      }
      .input-disabled {
        background-color: rgba(255, 255, 255, 0.1) !important;
        color: #ccc;
      }
    }
  }
}
</style>